<template>
  <div v-if="type === 'dropdown'" class="relative w-full" v-on-clickaway="() => { $emit('closeDropdown') }">
    <button class="w-full flex items-center gap-0.5 pl-2.5 py-1 pr-1 transition-colors rounded-r-md"
    :class="{ 'bg-neutral-25': showDropdown }"
    @click="$emit('toggleDropdown')">
      <BaseText type="label" size="sm" class="text-text-muted whitespace-nowrap">
        {{ dropdownSelection?.name || 'Select' }}
      </BaseText>
      <div class="ml-auto flex-shrink-0 transition-transform"
      :style="{ transform: showDropdown ? 'scaleY(-1)' : 'scaleY(1)' }">
        <ChevronIcon class="text-icon-normal" />
      </div>
    </button>
    <transition>
      <div v-if="showDropdown" class="value-dropdown flex flex-col p-1">
        <button v-for="(option, index) in dropdownOptions" :key="`dropdown-option-${index}`"
        class="option w-full flex items-center gap-2 p-1.5 rounded-md transition-colors duration-100
        text-neutral-alpha-650 hover:bg-neutral-alpha-50 hover:text-white"
        @click="selectDropdownOption(option)">
          <BaseText type="body" size="sm" class="whitespace-nowrap">
            {{ option.name }}
          </BaseText>
          <div class="w-5 h-5 ml-auto">
            <NewCheckmarkIcon v-if="dropdownSelection?.value === option.value" />
          </div>
        </button>
      </div>
    </transition>
  </div>
  <!-- Tag dropdown search -->
  <div v-else-if="type === 'search'" class="relative w-full" v-on-clickaway="() => { $emit('closeDropdown') }">
    <button class="w-full flex items-center gap-1 p-1 transition-colors rounded-r-md"
    :style="{ backgroundColor: tagColors[searchSelection?.color]?.fill || 'transparent' }"
    @click="$emit('toggleDropdown')">
      <div>
        <TagIcon
          class="w-5 h-5"
          :stroke="tagColors[searchSelection?.color]?.stroke"
          :fill="tagColors[searchSelection?.color]?.fill"
        />
      </div>
      <input
        :value="search"
        :type="isNumberType ? 'number' : 'text'"
        :placeholder="placeholder"
        class="value-input w-full" 
        :style="{ minWidth: inputMinWidth }"
        @input="handleSearch"
      />
    </button>
    <!-- TODO: add conditional to render various search popover options when needed -->
    <LensTagPopover :show="!hideTagPopover && search.length > 0" :search="search" @selectTag="selectTagOption" />
  </div>
  <div v-else class="w-full flex items-center gap-1 px-2 py-1">
    <BaseText v-if="type === 'currency'" type="body" size="sm" class="text-text-muted">
      {{ currencySymbol }}
    </BaseText>
    <BaseText v-if="type === 'relative_date'" type="body" size="sm" class="text-text-muted">
      last
    </BaseText>
    <input
      :value="inputValue"
      :type="isNumberType ? 'number' : 'text'"
      :placeholder="placeholder"
      class="value-input w-full max-w-none"
      :class="{'text-center': type === 'relative_date'}" 
      :style="{ minWidth: inputMinWidth }"
      @input="inputUpdateMethod"
      @blur="inputFormatMethod"
    />
    <BaseText v-if="type === 'percent'" type="body" size="sm" class="text-text-muted">
      %
    </BaseText>
    <BaseText v-if="type === 'relative_date'" type="body" size="sm" class="text-text-muted">
      days
    </BaseText>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway2'
import IsoCurrencies from '../../../utils/lens/ISOCurrencies.json'
import LensTagPopover from '../LensTagPopover.vue'
import TAG_COLORS from '../../../utils/TagColors'
import moment from 'moment'
// Icons
import ChevronIcon from '../../globals/Icons/ChevronIcon.vue'
import NewCheckmarkIcon from '../../globals/Icons/NewCheckmarkIcon.vue'
import TagIcon from '../../globals/Icons/TagIcon.vue'

export default {
  name: 'ConditionValueInput',
  mixins: [clickaway],
  components: {
    ChevronIcon,
    NewCheckmarkIcon,
    TagIcon,
    LensTagPopover
  },
  props: {
    value: {
      type: [Number, String],
      default: null
    },
    type: {
      type: String,
      required: true
    },
    placeholder: {
      type: String,
      default: ''
    },
    dropdownOptions: {
      type: Array,
      default: () => []
    },
    searchOptions: {
      type: Array,
      default: () => []
    },
    showDropdown: {
      type: Boolean,
      default: false
    },
    currencyRegion: {
      type: String,
      default: 'USD'
    }
  },
  data () {
    return {
      inputValue: '',
      dropdownSelection: null,
      searchSelection: null,

      search: '',
      tagColors: TAG_COLORS,
      hideTagPopover: false
    }
  },
  created () {
    if (this.type === 'dropdown') {
      this.dropdownSelection = this.dropdownOptions.find(option => option.value === this.value)
    } else if (this.type === 'search') {
      this.search = this.value || ''
      this.searchSelection = this.searchOptions.find(option => option.value === this.value)
      this.hideTagPopover = true
    } else {
      this.inputValue = `${this.value}`
      this.inputFormatMethod()
    }
  },
  watch: {
    value (newValue) {
      if (!newValue) this.inputValue = ''
    },
    type (oldType, newType) {
      // If the type changes, reset the value to null
      if (oldType !== newType) {
        this.$emit('updateValue', null)
      }
    },
    dropdownOptions (newOptions) {
      if (this.type === 'dropdown') {
        this.setDefaultDropdownSelection(newOptions)
      }
    }
  },
  computed: {
    isNumberType () {
      return ['number', 'currency', 'percent', 'relative_date'].includes(this.type)
    },
    inputUpdateMethod () {
      if (this.type === 'time') return this.handleTimeInput
      else if (this.type === 'date') return this.handleDateInput
      else if (this.type === 'relative_date') return this.handleRelativeDateInput
      return this.isNumberType ? this.handleNumberInput : this.handleTextInput
    },
    inputFormatMethod () {
      if (this.type === 'currency') return this.formatCurrencyInput
      else if (this.type === 'time') return this.formatTimeInput
      else if (this.type === 'date') return this.formatDateInput
      return () => {}
    },
    inputMinWidth () {
      if (this.type === 'percent' || this.type === 'search') return '40px'
      else if (['currency', 'number'].includes(this.type)) return '60px'
      else if (this.type === 'time') return '120px'
      else if (this.type === 'relative_date') return '28px'
      return '100px'
    },
    currencySymbol () {
      return IsoCurrencies?.[this.currencyRegion].symbolNative || '$'
    }
  },
  methods: {
    handleSearch (event) {
      this.searchSelection = null
      this.search = event.target.value
      this.hideTagPopover = false
    },
    selectTagOption (tag) {
      this.search = tag.value || ''
      this.hideTagPopover = true
      this.selectSearchOption(tag)
    },
    setDefaultDropdownSelection (options = null) {
      const initOption = (options ? options[0] : this.dropdownOptions[0]) || null
      this.dropdownSelection = initOption
      this.$emit('updateValue', initOption.value)
    },
    // Input Methods
    handleTextInput (event) {
      this.$emit('updateValue', event.target.value)
      this.inputValue = event.target.value
    },
    handleNumberInput (event) {
      const rawString = event.target.value
      if (!rawString.length) {
        this.$emit('updateValue', null)
        this.inputValue = ''
        return
      }
      const parsedValue = parseFloat(rawString)
      if (isNaN(parsedValue)) return
      this.$emit('updateValue', parsedValue)
      this.inputValue = rawString
    },
    handleRelativeDateInput (event) {
      const rawString = event.target.value

      // Allow only integers > 0 with at most 3 digits. Only allow 0 if it follows a non-zero digit.
      let digits = rawString.replace(/\D/g, '').slice(0, 3)
      if (digits[0] === '0') {
        digits = digits.slice(1)
      }
      if (!digits.length) {
        this.$emit('updateValue', null)
        this.inputValue = ''
        event.target.value = ''
        return
      }
      event.target.value = digits

      const parsedValue = parseInt(digits)
      if (isNaN(parsedValue) || parsedValue <= 0) return
      this.$emit('updateValue', parsedValue)
      this.inputValue = digits
    },
    formatCurrencyInput () {
      // Due to the number input handler, we assume the string parses to a number
      const numericValue = parseFloat(this.inputValue)
      if (isNaN(numericValue)) return
      const formattedValue = numericValue.toFixed(2)
      this.inputValue = formattedValue
    },

    // ----- Time Input/Parsing/Formatting Methods -----

    handleTimeInput (event) {
      // Ensure only numbers and at most one colon are allowed
      // Only allow a colon input if a number has already been entered

      let value = event.target.value
      const regex = /\d/.test(value) ? /[^0-9:]/g : /[^0-9]/g
      value = value.replace(regex, '')
      if (value.includes(':')) {
        const parts = value.split(':')
        if (parts.length > 2) {
          value = parts.slice(0, 2).join(':')
        }
      } 
      event.target.value = value
      
      const parsedValue = this.parseSecondsFromTime(value)
      if (isNaN(parsedValue)) return
      this.$emit('updateValue', parsedValue)
      this.inputValue = value
    },
    formatTimeInput () {
      const value = this.inputValue
      if (!value.length) {
        this.inputValue = ''
        return
      }
      let seconds = this.parseSecondsFromTime(value)
      this.inputValue = this.formatTimeFromSeconds(seconds)
    },
    parseSecondsFromTime (time) {
      const parts = time.split(':').reverse()
      return parts.reduce((acc, part, index) => {
        return acc + parseInt(part) * Math.pow(60, index)
      }, 0)
    },
    formatTimeFromSeconds (seconds) {
      const minutes = Math.floor(seconds / 60)
      const remainingSeconds = seconds % 60
      return `${minutes.toString().padStart(1, '0')}:${remainingSeconds.toString().padStart(2, '0')}`
    },

    // ----- Date Input/Parsing/Formatting Methods -----

    handleDateInput (event) {
      // Allow only digits to be input, at most 8 (YYYYMMDD)
      const raw = event.target.value
      const digits = raw.replace(/\D/g, '').slice(0, 8) // YYYYMMDD max

      // auto-format: "YYYY", "YYYY-MM", "YYYY-MM-DD"
      let out = digits.replace(
        /^(\d{0,4})(\d{0,2})(\d{0,2}).*$/,
        (_, y, m, d) => [y, m, d].filter(Boolean).join('-')
      )

      // keep a user-typed hyphen only if it’s at a valid boundary (after YYYY or YYYY-MM)
      const typedBoundaryHyphen = raw.endsWith('-') && (digits.length === 4 || digits.length === 6)
      if (typedBoundaryHyphen && !out.endsWith('-') && digits.length < 8) out += '-'

      event.target.value = out

      const parsedValue = formatDateString(out)
      this.$emit('updateValue', parsedValue)
      this.inputValue = out
    },
    formatDateInput () {
      const value = this.inputValue
      if (!value.length) {
        this.inputValue = ''
        return
      }
      const parsedValue = formatDateString(value)
      this.inputValue = parsedValue
    },

    // Dropdown Methods
    selectDropdownOption (option) {
      this.$emit('updateValue', option.value)
      this.dropdownSelection = option
      this.$emit('closeDropdown')
    },
    selectSearchOption (option) {
      this.$emit('updateValue', option.value)
      this.searchSelection = option
      this.$emit('closeDropdown')
    }
  }
}

// Examples (assuming today = 2025-10-09):
// '2025'          -> '2025-01-01'
// '2070'          -> '2025-10-09'  (capped to today when maxToday = true)
// '2025-15-42'    -> '2025-12-31'
// '2025-10'       -> '2025-10-01'
// '2025-00'       -> '2025-01-01'
const formatDateString = (dateString, maxToday = true) => {
  if (typeof dateString !== 'string') return ''

  // Pull out up to 3 numeric groups (YYYY, MM, DD) regardless of separators
  const parts = dateString.match(/\d+/g) || []
  if (parts.length === 0) return ''

  // Year: assume 4-digit (your input mask ensures YYYY). If shorter, pad left.
  let yearStr = parts[0].slice(0, 4)
  if (yearStr.length < 4) yearStr = yearStr.padStart(4, '0')
  let year = parseInt(yearStr, 10)

  // Month: default to 1 if missing; clamp to [1, 12]
  let month = parts[1] != null ? parseInt(parts[1], 10) : 1
  if (!Number.isFinite(month)) month = 1
  month = Math.min(Math.max(month, 1), 12)

  // Day: default to 1 if missing; clamp to [1, daysInMonth(year, month)]
  let day = parts[2] != null ? parseInt(parts[2], 10) : 1
  if (!Number.isFinite(day)) day = 1

  // Compute days in (possibly clamped) month
  const daysInMonth = moment({ year, month: month - 1, day: 1 }).daysInMonth()
  day = Math.min(Math.max(day, 1), daysInMonth)

  // Build the normalized date
  let normalized = moment({ year, month: month - 1, day }).startOf('day')

  // Cap to today if requested
  if (maxToday) {
    const today = moment().startOf('day')
    if (normalized.isAfter(today)) normalized = today
  }

  return normalized.format('YYYY-MM-DD')
}

</script>

<style scoped>
.value-input {
  /* Reset default styles */
  -webkit-appearance: none;  /* Remove default styling in WebKit browsers */
  -moz-appearance: none;     /* Remove default styling in Firefox */
  appearance: none;          /* Remove default styling in modern browsers */
  margin: 0;
  padding: 0;
  border: none;
  font-size: inherit;
  color: inherit;
  background-color: transparent;
  outline: none;

  /* Body/Small */
  font-family: Inter;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 142.857% */
}
.value-input:focus {
  outline: none;
  border: none;
  box-shadow: none;
}
.value-input::placeholder {
  opacity: 0.5;
  transition: opacity 100ms ease-in-out;
}
.value-input:focus::placeholder {
  opacity: 0.3;
}
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.value-dropdown {
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  border-radius: 10px;
  background-color: rgba(6, 7, 16, 0.92);
  backdrop-filter: blur(40px);
  z-index: 100;
}


.option {
  width: auto; /* fallback for calc-size */
  /* width: calc-size(auto, round(up, size, 2px)); */
}

/* ========= Vue <transition> classes ========= */

.v-enter-active, .v-leave-active {
  transition: opacity 150ms ease-in-out;
}
.v-enter-from, .v-enter, .v-leave-to {
  opacity: 0;
}
.v-enter-to, .v-leave-from {
  opacity: 1;
}

.tag-dropdown {
  position: absolute;
  top: calc(100%);
  left: 0;
  width: 200px;
  border-radius: 10px;
  background: #FFF;
  box-shadow: 0px 24px 32px -12px rgba(53, 57, 75, 0.12), 0px 1px 2px 0px rgba(162, 172, 186, 0.08), 0px 0px 0px 1px rgba(0, 56, 108, 0.04), 0px 1px 2px 0px rgba(162, 172, 186, 0.16), 0px 0px 0px 1px #EBEFF3;
  backdrop-filter: blur(5px);
  z-index: 9999;
}
</style>
